import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';

const FilterSidebar = ({ isOpen, onClose, availableFilters, activeFilters, onFilterChange }) => {
  const [selectedFieldKey, setSelectedFieldKey] = useState(availableFilters[0]?.key || 'name');

  const handleClearAll = () => {
    onFilterChange({});
  };

  const handleValueChange = (key, value) => {
    onFilterChange({ ...activeFilters, [key]: value });
  };

  const handleOperatorChange = (key, operator) => {
    // For demo purposes, we treat boolean flags (isEmpty) as special values or handle logic here
    // This simplifies the UI to match the image request
    if (operator === 'isEmpty') {
      onFilterChange({ ...activeFilters, [key]: '__EMPTY__' });
    } else if (operator === 'isNotEmpty') {
      onFilterChange({ ...activeFilters, [key]: '__NOT_EMPTY__' });
    } else {
      // Clear special flags if typing normally
      if (activeFilters[key] === '__EMPTY__' || activeFilters[key] === '__NOT_EMPTY__') {
        onFilterChange({ ...activeFilters, [key]: '' });
      }
    }
  };

  const currentField = availableFilters.find(f => f.key === selectedFieldKey);
  const currentValue = activeFilters[selectedFieldKey] || '';

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/10 backdrop-blur-sm z-40"
          />
          
          {/* Sidebar Container */}
          <motion.div 
            initial={{ x: "100%" }} 
            animate={{ x: 0 }} 
            exit={{ x: "100%" }} 
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-2xl z-50 flex flex-col border-l border-slate-200"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <h2 className="text-lg font-bold text-slate-800">Bruk filter</h2>
              <button 
                onClick={handleClearAll} 
                className="text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors"
              >
                Nullstill alt
              </button>
            </div>

            {/* Main Content Area - Split View */}
            <div className="flex flex-1 overflow-hidden">
              
              {/* LEFT COLUMN: Field List */}
              <div className="w-1/3 border-r border-slate-100 overflow-y-auto bg-slate-50/50">
                {availableFilters.map((field) => {
                  const isActive = selectedFieldKey === field.key;
                  const hasValue = activeFilters[field.key] && activeFilters[field.key] !== '';
                  
                  return (
                    <button
                      key={field.key}
                      onClick={() => setSelectedFieldKey(field.key)}
                      className={`w-full text-left px-5 py-3 text-sm font-medium transition-all flex items-center justify-between group ${
                        isActive 
                          ? 'bg-white text-blue-600 border-l-4 border-blue-600 shadow-sm' 
                          : 'text-slate-600 hover:bg-slate-100 border-l-4 border-transparent'
                      }`}
                    >
                      <span>{field.label}</span>
                      {hasValue && (
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* RIGHT COLUMN: Filter Controls */}
              <div className="w-2/3 p-6 overflow-y-auto bg-white">
                {currentField && (
                  <div className="space-y-6">
                    <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4">
                      {currentField.label}
                    </h3>

                    {/* Operators (checkbox style from image) */}
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={currentValue === '__EMPTY__'}
                          onChange={() => handleOperatorChange(currentField.key, currentValue === '__EMPTY__' ? 'clear' : 'isEmpty')}
                          className="w-5 h-5 border-slate-300 rounded text-blue-600 focus:ring-blue-500 transition-all"
                        />
                        <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">Er tom (Ingen verdi)</span>
                      </label>
                      
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input 
                          type="checkbox" 
                          checked={currentValue === '__NOT_EMPTY__'}
                          onChange={() => handleOperatorChange(currentField.key, currentValue === '__NOT_EMPTY__' ? 'clear' : 'isNotEmpty')}
                          className="w-5 h-5 border-slate-300 rounded text-blue-600 focus:ring-blue-500 transition-all"
                        />
                        <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">Er ikke tom</span>
                      </label>
                    </div>

                    <div className="w-full h-px bg-slate-100 my-4"></div>

                    {/* Value Input */}
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-slate-400">Har verdi</label>
                      
                      {currentField.type === 'select' ? (
                        <select 
                          value={currentValue === '__EMPTY__' || currentValue === '__NOT_EMPTY__' ? '' : currentValue}
                          onChange={(e) => handleValueChange(currentField.key, e.target.value)}
                          disabled={currentValue === '__EMPTY__' || currentValue === '__NOT_EMPTY__'}
                          className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-400 bg-white"
                        >
                          <option value="">Velg...</option>
                          {currentField.options?.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="space-y-2">
                          <label className="text-xs font-semibold text-slate-700">Inneholder</label>
                          <input 
                            type={currentField.type || 'text'}
                            value={currentValue === '__EMPTY__' || currentValue === '__NOT_EMPTY__' ? '' : currentValue}
                            onChange={(e) => handleValueChange(currentField.key, e.target.value)}
                            disabled={currentValue === '__EMPTY__' || currentValue === '__NOT_EMPTY__'}
                            className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 outline-none transition-all disabled:bg-slate-50 disabled:text-slate-400"
                            placeholder="Søk..."
                          />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-3 bg-slate-50">
              <button 
                onClick={onClose} 
                className="px-6 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-200 rounded-xl transition-all"
              >
                Avbryt
              </button>
              <button 
                onClick={onClose} 
                className="px-8 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-lg shadow-blue-200 transition-all"
              >
                Vis resultater
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default FilterSidebar;